import React, { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { leadAPI } from '../utils/api';
import { CheckCircle2, Loader2, Camera } from 'lucide-react';

const EVENT_TYPES = ['Pre-Wedding Events', 'Wedding', 'Reception', 'Engagement', 'Others'];
const COVERAGE = ['Photo', 'Video', 'Photo + Video'];

const Inquiry = () => {
  const [searchParams] = useSearchParams();
  const embed = useMemo(() => searchParams.get('embed') === '1', [searchParams]);

  const [form, setForm] = useState({
    name: '', email: '', phone: '', instagram: '', event_type: '', event_date: '',
    venue: '', referred_by: '', coverage_type: '', message: '', company: '',
  });
  const [consent, setConsent] = useState(false);
  const [status, setStatus] = useState('idle'); // idle | sending | done | error
  const [error, setError] = useState('');

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      setError('Please fill in your name, email and phone.'); return;
    }
    if (!consent) { setError('Please tick the consent box so we can respond to you.'); return; }
    setStatus('sending');
    setError('');
    try {
      await leadAPI.submitPublic(form);
      setStatus('done');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Something went wrong. Please try again.');
      setStatus('error');
    }
  };

  const inputCls = 'w-full px-4 py-3 border border-input bg-transparent text-foreground placeholder:text-muted-foreground rounded-lg focus:outline-none focus:border-gold transition-colors';

  const card = (
    <div className="w-full max-w-xl mx-auto" data-testid="inquiry-form-container">
      {!embed && (
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gold/10 text-gold mb-4">
            <Camera size={26} />
          </div>
          <h1 className="text-3xl sm:text-4xl font-serif mb-2">Contact Us</h1>
          <p className="text-sm uppercase tracking-[0.25em] text-muted-foreground">Your Story Begins Here</p>
        </div>
      )}

      {status === 'done' ? (
        <div className="card p-10 text-center" data-testid="inquiry-success">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-500/10 text-green-600 mb-4">
            <CheckCircle2 size={34} />
          </div>
          <h2 className="text-2xl font-serif mb-2">Thank you!</h2>
          <p className="text-muted-foreground">We've received your inquiry and we'll be in touch shortly. 🤍</p>
        </div>
      ) : (
        <form onSubmit={submit} className="card p-6 sm:p-8 space-y-4" data-testid="inquiry-form">
          {/* Honeypot: hidden from humans */}
          <input
            type="text" tabIndex="-1" autoComplete="off"
            value={form.company} onChange={set('company')}
            style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px' }}
            aria-hidden="true"
          />

          <div>
            <label className="block text-sm mb-1.5">Your name *</label>
            <input className={inputCls} value={form.name} onChange={set('name')} placeholder="Full name" data-testid="inquiry-name" required />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm mb-1.5">Email address *</label>
              <input type="email" className={inputCls} value={form.email} onChange={set('email')} placeholder="you@email.com" data-testid="inquiry-email" required />
            </div>
            <div>
              <label className="block text-sm mb-1.5">Phone *</label>
              <input className={inputCls} value={form.phone} onChange={set('phone')} placeholder="+1 555 000 0000" data-testid="inquiry-phone" required />
            </div>
          </div>

          <div>
            <label className="block text-sm mb-1.5">Instagram</label>
            <input className={inputCls} value={form.instagram} onChange={set('instagram')} placeholder="@yourhandle" data-testid="inquiry-instagram" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm mb-1.5">Event type</label>
              <select className={inputCls} value={form.event_type} onChange={set('event_type')} data-testid="inquiry-event-type">
                <option value="">Select…</option>
                {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm mb-1.5">Event date</label>
              <input type="date" className={inputCls} value={form.event_date} onChange={set('event_date')} data-testid="inquiry-event-date" />
            </div>
          </div>

          <div>
            <label className="block text-sm mb-1.5">Venue / location</label>
            <input className={inputCls} value={form.venue} onChange={set('venue')} placeholder="Venue or city" data-testid="inquiry-venue" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm mb-1.5">Coverage type</label>
              <select className={inputCls} value={form.coverage_type} onChange={set('coverage_type')} data-testid="inquiry-coverage">
                <option value="">Select…</option>
                {COVERAGE.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm mb-1.5">Referred by</label>
              <input className={inputCls} value={form.referred_by} onChange={set('referred_by')} placeholder="How did you hear about us?" data-testid="inquiry-referred" />
            </div>
          </div>

          <div>
            <label className="block text-sm mb-1.5">Message</label>
            <textarea className={inputCls} rows="3" value={form.message} onChange={set('message')} placeholder="Tell us about your day…" data-testid="inquiry-message" />
          </div>

          <label className="flex items-start gap-2.5 text-xs text-muted-foreground cursor-pointer" data-testid="inquiry-consent-label">
            <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 w-4 h-4" data-testid="inquiry-consent" />
            <span>By sending this form, I consent to having this website store my submitted information so they can respond to my inquiry.</span>
          </label>

          {error && <p className="text-sm text-red-500" data-testid="inquiry-error">{error}</p>}

          <button
            type="submit"
            disabled={status === 'sending'}
            className="w-full bg-primary text-primary-foreground py-3.5 rounded-full font-medium transition-all active:scale-[0.98] disabled:opacity-60 flex items-center justify-center gap-2"
            data-testid="inquiry-submit"
          >
            {status === 'sending' ? (<><Loader2 size={18} className="animate-spin" /> Sending…</>) : 'Send Message'}
          </button>
        </form>
      )}
    </div>
  );

  if (embed) {
    return <div className="p-4 bg-background" data-testid="inquiry-embed">{card}</div>;
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4 sm:p-8" data-testid="inquiry-page">
      {card}
    </div>
  );
};

export default Inquiry;
